import { Link } from "react-router-dom";
import { FolderIcon, EyeIcon } from "@heroicons/react/24/solid";
import type { ProjectCardProps } from "./projectCard.types";
import { formatDate, getStatusIcon, truncateDescription } from "./projectCard.definitions";
import { ProjectCardMenu, ProjectProgressBar, ProjectStatusBadge } from "./components";

export function ProjectCard({
  project,
  viewMode,
  onDelete,
  isDeleting,
}: ProjectCardProps) {
  const StatusIcon = getStatusIcon(project.status);

  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-xl p-6 border border-primary-100 shadow-raro hover:shadow-raro-lg transition-all duration-200 group hover:border-primary-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl ring-1 ring-primary-100">
              <FolderIcon className="w-7 h-7 text-primary-600" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/projects/${project.id}`}
                    className="block text-xl font-bold text-raro-gray-900 font-montserrat hover:text-primary-600 transition-colors mb-1 truncate"
                  >
                    {project.name}
                  </Link>
                  <p className="text-sm text-raro-gray-600 line-clamp-2 leading-relaxed">
                    {truncateDescription(project.description, 150)}
                  </p>
                </div>
                
                <div className="flex-shrink-0 ml-4">
                  <ProjectCardMenu
                    projectId={project.id}
                    onDelete={onDelete}
                    isDeleting={isDeleting}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center space-x-3">
                  <ProjectStatusBadge status={project.status} />
                  <span className="text-xs text-raro-gray-400">•</span>
                  <p className="text-xs text-raro-gray-500">
                    {formatDate(project.updatedAt)}
                  </p>
                </div>
                
                <Link
                  to={`/projects/${project.id}`}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                >
                  <EyeIcon className="w-3 h-3 mr-1" />
                  Ver Detalhes
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <ProjectProgressBar currentPhase={project.currentPhase} />
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="bg-white rounded-xl p-6 border border-primary-100 shadow-raro hover:shadow-raro-lg transition-all duration-200 group hover:border-primary-200 hover:-translate-y-1">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl ring-1 ring-primary-100">
          <FolderIcon className="w-7 h-7 text-primary-600" />
        </div>

        <ProjectCardMenu
          projectId={project.id}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </div>

      <div className="mb-5">
        <Link
          to={`/projects/${project.id}`}
          className="block text-lg font-bold text-raro-gray-900 font-montserrat hover:text-primary-600 transition-colors mb-2 leading-tight"
        >
          {project.name}
        </Link>
        <p className="text-sm text-raro-gray-600 line-clamp-3 leading-relaxed">
          {truncateDescription(project.description, 120)}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <ProjectStatusBadge status={project.status} />
          <p className="text-xs text-raro-gray-500">
            {formatDate(project.updatedAt)}
          </p>
        </div>

        <ProjectProgressBar currentPhase={project.currentPhase} />
        
        <Link
          to={`/projects/${project.id}`}
          className="inline-flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
        >
          <EyeIcon className="w-4 h-4 mr-2" />
          Ver Detalhes
        </Link>
      </div>
    </div>
  );
}